/**
 * AppShell — extensible layout component.
 *
 * Renders a fixed left sidebar, top header bar, and main content Outlet.
 * All route modules render INTO this shell via React Router's <AccessRoute><OperationNotices /><Outlet /></AccessRoute>.
 *
 * ⚠️ Requires a React Router ancestor (BrowserRouter or MemoryRouter).
 * ⚠️ Does NOT import or call any Amplify Auth methods — pure layout only.
 *    (BRD 06 UC-2 added a logout control, which calls our own backend's
 *    /auth/logout directly, same as the rest of this app -- still no
 *    Amplify involved.)
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { APP_NAVIGATION, visibleNavigation, type NavItem } from '../../lib/navigation';
import { getTokens, clearTokens } from '../../lib/auth-storage';
import { logout } from '../../lib/auth-api';
import { getEmailFromIdToken } from '../../lib/jwt';
import { AccessProvider } from '../../lib/access/AccessProvider';
import { useAccess } from '../../lib/access/access.context';
import { AccessRoute } from './AccessRoute';
import { WarningBell } from '../warnings/WarningBell';
import { OperationNotices } from '../workflows/OperationNotices';
import { BrandLogo } from '../ui/brand-logo';

function NavItemLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const relatedActive = item.activePaths?.includes(location.pathname) ?? false;
  const childActive =
    item.children?.some((child) => location.pathname.startsWith(child.path)) ?? false;
  const [expanded, setExpanded] = useState(childActive);
  useEffect(() => {
    if (childActive) setExpanded(true);
  }, [childActive]);
  if (item.children?.length)
    return (
      <li>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={`nav-${item.id}`}
          onClick={() => setExpanded(!expanded)}
          className={`flex w-full items-center justify-between rounded-field border-l-2 px-3 py-2 text-section font-medium transition-colors ${childActive ? 'border-accent text-white' : 'border-transparent text-on-dark-70 hover:bg-primary-900-hover hover:text-white'}`}
        >
          {item.label}
          <span aria-hidden="true">{expanded ? '−' : '+'}</span>
        </button>
        {expanded && (
          <ul id={`nav-${item.id}`} className="ml-4 mt-2 space-y-1 border-l border-white/15 pl-2">
            {item.children.map((child) => (
              <NavItemLink key={child.id} item={child} />
            ))}
          </ul>
        )}
      </li>
    );
  const linkClass = (active: boolean) =>
    `flex items-center gap-2 rounded-field border-l-2 px-3 py-2 text-section font-medium transition-colors ${active ? 'bg-primary-900-hover border-accent text-white' : 'border-transparent text-on-dark-70 hover:bg-primary-900-hover hover:text-white'}`;
  const label = <>{item.icon && <item.icon />}<span>{item.label}</span></>;
  return (
    <li>
      {relatedActive ? <Link to={item.path} aria-current="page" className={linkClass(true)}>{label}</Link> : (
      <NavLink
        to={item.path}
        end={item.id !== 'masterdata' && item.id !== 'inventory-planning'}
        className={({ isActive }) => linkClass(isActive)}
      >
        {label}
      </NavLink>
      )}
    </li>
  );
}

export function AppShell() {
  return (
    <AccessProvider showMessages={false}>
      <Shell />
    </AccessProvider>
  );
}
function Shell() {
  const access = useAccess();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [mobileNavigation, setMobileNavigation] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMobileNavigation(false);
  }, [location.pathname]);

  // Real avatar, not the old static "U" placeholder. Reads the ID
  // token directly (synchronous, no extra request) rather than calling
  // /me: confirmed live that the ACCESS token in this pool carries
  // neither `email` nor `username`, only `sub` -- an earlier version of
  // this fix used /me (which decodes the access token) and fell back to
  // showing a meaningless letter from a UUID. ID tokens are the
  // OIDC-standard place for profile claims like email.
  const tokens = getTokens();
  const currentUserEmail = tokens ? getEmailFromIdToken(tokens.idToken) : null;

  // BRD 06 UC-2: tokens are cleared client-side regardless of whether the
  // backend call succeeds (AC-3) -- a dead/unreachable backend must never
  // leave the user stuck looking logged in.
  async function handleLogout() {
    setLoggingOut(true);
    try {
      const tokens = getTokens();
      if (tokens) {
        await logout(tokens.accessToken);
      }
    } catch {
      // Intentionally ignored -- see the doc comment above. The finally
      // block below still runs.
    } finally {
      clearTokens();
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background sm:flex-row">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-field focus:bg-card focus:p-3"
      >
        Skip to content
      </a>
      {/* Sidebar — kachi-iro (primary-900), the app's real "brand chrome".
          The shu-iro accent is reserved for the active nav item's left
          rule only, per the design system's "spend it once per screen"
          rule — never as the sidebar background or a general fill. */}
      <aside className="flex w-full flex-shrink-0 flex-col bg-primary-900 sm:w-64">
        {/* Logo / Wordmark */}
        <div className="flex h-16 min-w-0 flex-shrink-0 items-center justify-between gap-3 border-b border-white/10 px-5 sm:px-6">
          <BrandLogo variant="compact" />
          <button
            type="button"
            aria-expanded={mobileNavigation}
            aria-controls="main-navigation"
            onClick={() => setMobileNavigation((value) => !value)}
            className="shrink-0 rounded-field border border-white/20 px-3 py-2 text-label font-medium text-on-dark-70 hover:bg-primary-900-hover hover:text-white sm:hidden"
          >
            {mobileNavigation ? 'Close menu' : 'Menu'}
          </button>
        </div>

        {/* Navigation */}
        <nav
          id="main-navigation"
          aria-label="Main navigation"
          className={`${mobileNavigation ? 'block' : 'hidden'} max-h-[45vh] flex-1 overflow-y-auto px-3 py-4 sm:block sm:max-h-none`}
        >
          <ul className="space-y-1">
            {visibleNavigation(APP_NAVIGATION, access.allows).map((item) => (
              <NavItemLink key={item.id} item={item} />
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-border bg-card px-6">
          <div className="text-body font-medium text-foreground">Nissei workspace</div>
          <div className="flex items-center gap-4">
            <WarningBell />
            <button
              type="button"
              onClick={() => void handleLogout()}
              disabled={loggingOut}
              className="text-body font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              {loggingOut ? 'Signing out…' : 'Log out'}
            </button>
            {/* Real avatar initial from the logged-in user's ID token
                (see the comment above) -- falls back to a plain generic
                mark if the token can't be decoded or lacks an email
                claim, never a fake or meaningless letter. */}
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-body font-medium text-primary-900"
              title={currentUserEmail ?? undefined}
              aria-label={currentUserEmail ? `Signed in as ${currentUserEmail}` : 'User account'}
            >
              {currentUserEmail ? currentUserEmail.charAt(0).toUpperCase() : '—'}
            </div>
          </div>
        </header>

        {/* Page content — flex flex-col so a page can opt its own root
            into flex-1 and grow to fill leftover vertical space (e.g.
            ProductsPage's table card) instead of stopping at its content
            height with blank page background below it. Pages that don't
            opt in are unaffected: a lone block child of a column flex
            container keeps its natural height. */}
        <main
          id="main-content"
          tabIndex={-1}
          className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-background px-4 pb-6 text-foreground lg:px-8"
        >
          <AccessRoute>
            <OperationNotices />
            <Outlet />
          </AccessRoute>
        </main>
      </div>
    </div>
  );
}

export default AppShell;
